import { t } from "@travily/i18n";
import { formatNativePrice } from "@ui/lib/format-price";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

const STRIPE_FEE_PERCENT = 0.029;
const STRIPE_FEE_FIXED_CENTS = 30;
// Rendered as the "you keep" estimate marker. Held as a const so it reaches JSX as an identifier
// rather than a string literal (which i18next/no-literal-string flags — it's a math symbol, not copy).
const APPROX_SYMBOL = "≈";

export interface FeeTransparencyProps {
  priceCents: number | null;
  currency: string;
  platformFeeRate: number;
}

/**
 * A payout ledger for the pricing stage: lists the price, the fees subtracted from it, and — as the
 * hero line — what the seller keeps per sale. Sits on a brand-green surface rather than a neutral
 * note box; the take-home number is what the seller is here to see, so it leads. Shows a single
 * quiet prompt until a price is entered, then grows into the breakdown: the green surface eases its
 * height (`layout`) while the prompt crossfades to the ledger (`AnimatePresence`), so first-keystroke
 * and clear-to-empty feel continuous rather than a hard swap. Motion is dropped under reduced motion.
 */
export function FeeTransparency({ priceCents, currency, platformFeeRate }: FeeTransparencyProps) {
  const reducedMotion = useReducedMotion() ?? false;
  const hasPrice = priceCents !== null && priceCents > 0;

  return (
    <motion.div
      layout={reducedMotion ? false : "size"}
      transition={{ duration: reducedMotion ? 0 : 0.28, ease: "easeOut" }}
      className="relative overflow-hidden rounded-2xl bg-green-50 px-5 py-4"
    >
      <AnimatePresence initial={false} mode="popLayout">
        {hasPrice ? (
          <motion.dl
            key="ledger"
            className="text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
          >
            <Ledger currency={currency} platformFeeRate={platformFeeRate} priceCents={priceCents} />
          </motion.dl>
        ) : (
          <motion.p
            key="empty"
            className="text-center text-green-900/70 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.18 }}
          >
            {t("components.views.shared.fee_transparency.empty")}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Ledger({
  priceCents,
  currency,
  platformFeeRate,
}: { priceCents: number } & Omit<FeeTransparencyProps, "priceCents">) {
  const ratePercent = Math.round(platformFeeRate * 100);
  const stripeFee = Math.round(priceCents * STRIPE_FEE_PERCENT + STRIPE_FEE_FIXED_CENTS);
  const platformFee = Math.round(priceCents * platformFeeRate);
  const netCents = Math.max(0, priceCents - stripeFee - platformFee);

  return (
    <>
      <LedgerRow
        label={t("components.views.shared.fee_transparency.list_price")}
        value={formatNativePrice(priceCents, currency)}
      />
      <LedgerRow
        label={t("components.views.shared.fee_transparency.stripe_fee")}
        value={`−${formatNativePrice(stripeFee, currency)}`}
        muted
      />
      <LedgerRow
        label={t("components.views.shared.fee_transparency.platform_fee", { rate: ratePercent })}
        value={`−${formatNativePrice(platformFee, currency)}`}
        muted
      />
      <div className="mt-3 flex items-baseline justify-between border-green-200/70 border-t pt-3">
        <dt className="font-semibold text-green-900">{t("components.views.shared.fee_transparency.net")}</dt>
        <dd className="font-bold text-green-900 text-xl tabular-nums">
          {/* The net is only ever an estimate — Stripe's real fee varies by card/country — so the ≈
              marks the hero as approximate at the number itself. List price and the exact Travily fee
              stay unmarked; the caption below carries the why. */}
          <span className="mr-0.5 font-normal text-green-900/50">{APPROX_SYMBOL}</span>
          {formatNativePrice(netCents, currency)}
        </dd>
      </div>
      <p className="mt-2 text-green-900/60 text-xs leading-snug">
        {t("components.views.shared.fee_transparency.caption")}
      </p>
    </>
  );
}

function LedgerRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <dt className="text-neutral-600">{label}</dt>
      <dd className={`tabular-nums ${muted ? "text-neutral-500" : "text-neutral-700"}`}>{value}</dd>
    </div>
  );
}
